import { z } from 'zod';
import { Event, EventSchema } from '@/types/events';
import { mockEvents } from '../data/mockEvents';
import { validateEvent } from '../utils/validation';
import { calculateBudgetMetrics } from '../utils/metrics';

const STORAGE_KEY = 'psa_events';
const VERSION_KEY = 'psa_events_version';
const CURRENT_VERSION = '1.0';

class EventStorageService {
  private static instance: EventStorageService;

  private constructor() {
    this.initializeStorage();
  }

  private initializeStorage(): void {
    try {
      const version = localStorage.getItem(VERSION_KEY);
      const storedEvents = localStorage.getItem(STORAGE_KEY);

      // Initialize with mock data if no events exist or version mismatch
      if (!storedEvents || version !== CURRENT_VERSION) {
        try {
          // Validate and transform mock events
          const validatedMockEvents = mockEvents.map(event => {
            try {
              return this.validateAndEnrichEvent(event);
            } catch (err) {
              console.error('Failed to validate mock event:', err);
              return null;
            }
          }).filter((event): event is Event => event !== null);

          localStorage.setItem(STORAGE_KEY, JSON.stringify(validatedMockEvents));
          localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
        } catch (err) {
          console.error('Failed to validate mock events:', err);
          localStorage.setItem(STORAGE_KEY, '[]');
          localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
        }
      }
    } catch (error) {
      console.error('Storage initialization error:', error);
      localStorage.setItem(STORAGE_KEY, '[]');
      localStorage.setItem(VERSION_KEY, CURRENT_VERSION);
    }
  }

  private setEvents(events: Event[]): void {
    try {
      const validatedEvents = z.array(EventSchema).parse(events);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(validatedEvents));
    } catch (error) {
      console.error('Error saving events:', error);
    }
  }

  public static getInstance(): EventStorageService {
    if (!EventStorageService.instance) {
      EventStorageService.instance = new EventStorageService();
    }
    return EventStorageService.instance;
  }

  private validateAndEnrichEvent(event: Event): Event {
    try {
      const validatedEvent = validateEvent(event);
      // Ensure metrics are properly initialized
      if (!validatedEvent.metrics) {
        validatedEvent.metrics = {
          attendance: 0,
          satisfaction: 0,
          budget: {
            estimated: 0,
            actual: null,
            variance: 0
          }
        };
      }
      return validatedEvent;
    } catch (err) {
      console.error('Event validation failed:', err);
      throw err;
    }
  }

  public getEvents(): Event[] {
    try {
      const events = localStorage.getItem(STORAGE_KEY);
      if (!events) return mockEvents;
      
      const parsedEvents = JSON.parse(events);
      const validatedEvents = parsedEvents.map((event: any) => {
        try {
          return this.validateAndEnrichEvent(event as Event);
        } catch (err) {
          console.error('Event validation failed:', err);
          return null;
        }
      }).filter((event: Event | null): event is Event => event !== null);
      
      return validatedEvents;
    } catch (err) {
      console.error('Error retrieving events:', err);
      return mockEvents;
    }
  }

  public saveEvent(event: Event): Event | null {
    try {
      const validatedEvent = this.validateAndEnrichEvent(event);
      const events = this.getEvents();
      
      // Check for duplicate ID
      if (events.some(e => e.id === event.id)) {
        throw new Error('Event with this ID already exists');
      }
      
      const updatedEvents = [...events, validatedEvent];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEvents));
      return validatedEvent;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save event';
      console.error('Error saving event:', errorMessage);
      return null;
    }
  }

  public updateEvent(updatedEvent: Event): Event | null {
    try {
      const validatedEvent = this.validateAndEnrichEvent(updatedEvent);
      const currentEvents = this.getEvents();
      const eventIndex = currentEvents.findIndex(e => e.id === validatedEvent.id);
      
      if (eventIndex === -1) {
        throw new Error(`Event with ID ${validatedEvent.id} not found`);
      }

      const updatedEvents = [...currentEvents];
      updatedEvents[eventIndex] = validatedEvent;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEvents));
      
      return validatedEvent;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update event';
      console.error('Error updating event:', errorMessage);
      return null;
    }
  }

  public updateEventTasks(eventId: string, tasks: Event['tasks']): Event | null {
    try {
      const events = this.getEvents();
      const eventIndex = events.findIndex(e => e.id === eventId);
      
      if (eventIndex === -1) {
        throw new Error(`Event with ID ${eventId} not found`);
      }

      const updatedEvent = {
        ...events[eventIndex],
        tasks,
        updatedAt: new Date().toISOString()
      };

      const validatedEvent = this.validateAndEnrichEvent(updatedEvent);
      events[eventIndex] = validatedEvent;
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(events));
      return validatedEvent;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update event tasks';
      console.error('Error updating event tasks:', errorMessage);
      return null;
    }
  }

  public deleteEvent(id: string): boolean {
    try {
      const events = this.getEvents();
      const updatedEvents = events.filter(event => event.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEvents));
      return true;
    } catch (err) {
      console.error('Error deleting event:', err);
      return false;
    }
  }

  public clearStorage(): void {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(VERSION_KEY);
    this.initializeStorage();
  }
}

export const eventStorage = EventStorageService.getInstance();